import { Form, Formik } from 'formik'
import { useRouter } from 'next/router'
import { useMemo } from 'react'

import { FormLayoutProvider } from 'src/providers/FormLayoutProvider'
import { GoogleMapsProvider } from 'src/providers/GoogleMapsProvider'
import { NotificationType, notify } from 'src/providers/NotificationProvider'

import { ButtonRebrand } from 'src/components/atoms/ButtonRebrand'
import { Card } from 'src/components/atoms/Card'
import { InternalLink, OnClickLink } from 'src/components/atoms/Link'
import { Heading3, ParagraphSmaller } from 'src/components/atoms/Typography'
import { FormField } from 'src/components/molecules/FormField'
import { AccountFormFieldSets } from 'src/components/organisms/forms/AccountForm/AccountFormFieldSets'

import {
  useUpdateAccountMutation,
  namedOperations,
  useListRegionsAndDistrictsQuery,
  UpdateAccountMutationVariables,
  useListRegionsQuery,
  useListDistrictsQuery,
  useUpdateAddressMutation
} from 'src/config/generated/graphql'
import { viewAccountDetailPath, viewAccountProfilePath } from 'src/config/paths'
import { GetAccountType, AccountType } from 'src/types/account'
import { FormFieldTypes } from 'src/types/form'
import { GeoData } from 'src/types/map'
import { accountTypeOptions } from 'src/utils/account'
import { matchRegionDistrictByName } from 'src/utils/map'
import { trpc } from 'src/utils/trpc'

import { MapLocationSearch } from '../../MapLocationSearch'
import { businessSizeOptions, departmentOptions } from '../AccountForm/helper'
import { transformFormData } from './helper'
import { UpdateAccountDetailsFormValues } from './types'
import { validationSchema } from './validationSchema'

const DEFAULT_AVATAR = 'assets/img/default-account.png'
const DEFAULT_BANNER = 'assets/img/banners/account-banner-1.jpg'

export interface UpdateAccountDetailsFormProps {
  account?: GetAccountType
}

export const UpdateAccountDetailsForm = ({
  account
}: UpdateAccountDetailsFormProps) => {
  const { data: regionsData } = useListRegionsQuery()
  const { data: districtsData } = useListDistrictsQuery()
  const router = useRouter()

  const [updateAddress] = useUpdateAddressMutation({
    onCompleted: () => {
      notify({
        message: `Successfully updated account`,
        type: NotificationType.success
      })
      router.push(viewAccountDetailPath)
    }
  })
  const { data: regionsAndDistricts } = useListRegionsAndDistrictsQuery()
  const { invalidateQueries } = trpc.useContext()

  const [updateAccountDetails] = useUpdateAccountMutation({
    refetchQueries: [
      namedOperations.Query.getAccount,
      namedOperations.Query.getUserAndMemberships
    ],
    onCompleted: () => {
      notify({
        message: `Successfully updated account`,
        type: NotificationType.success
      })
      router.push(viewAccountDetailPath)
    }
  })
  const getManualAddressOption = (
    address?: UpdateAccountDetailsFormValues['Address']
  ) => {
    if (address?.Geo) return 'No'
    if (address?.StreetNumber && address.StreetName) return 'Yes'
    return 'No'
  }

  const initialValues = useMemo<UpdateAccountDetailsFormValues>(
    () => ({
      Avatar: account?.Avatar || DEFAULT_AVATAR,
      AvatarFile: [],
      Banner: account?.Banner || DEFAULT_BANNER,
      BannerFile: [],
      BusinessType: account?.BusinessType || '',
      Description: account?.Description || '',
      DistrictId: account?.DistrictId || null,
      Email: account?.Email || '',
      FocusArea: account?.FocusArea || '',
      GovType: account?.GovType || '',
      Mobile: account?.Mobile || '',
      Name: account?.Name || '',
      OrgType: account?.OrgType || '',
      Phone: account?.Phone || '',
      RegionId: account?.RegionId || 'none',
      SchoolType: account?.SchoolType || '',
      Sector: account?.Sector || '',
      Type: account?.Type || AccountType.Unknown,
      PlanterHidden: account?.Planter?.IsHidden,
      FunderHidden: account?.Funder?.IsHidden,
      FunderName: account?.Funder?.Name || account?.Name,
      PlanterName: account?.Planter?.Name || account?.Name,
      UseNameOfAccount:
        account?.Funder?.Name === account?.Name ||
        account?.Planter?.Name === account?.Name
          ? 'Yes'
          : 'No',
      Address: account?.Addresses?.[0]
        ? {
            City: account.Addresses[0].City,
            Country: account.Addresses[0].Country,
            District: account.Addresses[0].District,
            Geo: account.Addresses[0].Geo,
            Place: account.Addresses[0].Place,
            PostalCode: account.Addresses[0].PostalCode,
            Region: account.Addresses[0].Region,
            StreetName: account.Addresses[0].StreetName,
            StreetNumber: account.Addresses[0].StreetNumber
          }
        : null,
      ManualAddress: getManualAddressOption(
        account?.Addresses?.[0] as UpdateAccountDetailsFormValues['Address']
      ),
      ContactFirstname: account?.ContactFirstname || '',
      ContactLastname: account?.ContactLastname || '',
      Department: account?.Department || '',
      JobTitle: account?.Memberships?.[0]?.User?.JobTitle || '',
      BusinessSize: account?.BusinessSize || undefined
    }),
    [account]
  )

  const handleSubmit = async (formValues: UpdateAccountDetailsFormValues) => {
    const variables: UpdateAccountMutationVariables = {
      id: account?.Id,
      attrs: transformFormData(formValues, regionsAndDistricts!),
      userAttrs: formValues.JobTitle ? { JobTitle: formValues.JobTitle } : {}
    }

    await updateAccountDetails({
      variables,
      onCompleted: async () => {
        await updateAddress({
          variables: {
            input: {
              Id: account?.Addresses[0]?.Id,
              AccountId: account?.Id,
              ...formValues.Address
            }
          }
        })
        await invalidateQueries(['accounts.getByUri', { uri: account?.Uri! }])
      }
    })
  }

  return (
    <Card size='large' corner='square'>
      <FormLayoutProvider margin='small'>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {({ values, setFieldValue, dirty }) => {
            const setRegionDistrict = (geoData?: GeoData) => {
              const regionDistrict = matchRegionDistrictByName(
                geoData,
                regionsData?.Region,
                districtsData?.District
              )

              if (regionDistrict?.region) {
                setFieldValue('RegionId', regionDistrict.region.Id)
              }

              if (regionDistrict?.district) {
                setFieldValue('DistrictId', regionDistrict.district.Id)
              }
            }

            return (
              <GoogleMapsProvider>
                <Form>
                  <div className='flex flex-col gap-4 md:gap-8'>
                    <div>
                      <Heading3
                        id='details'
                        className='text-[28px] leading-[39px] font-semibold m-0! mb-5!'
                      >
                        Account information
                      </Heading3>
                      <FormField
                        id='Name'
                        label='Name for this account'
                        type={FormFieldTypes.text}
                        description='Changing this will update how we refer to this account e.g. in your receipts and communications. You can change how it gets represented as your funder or a planter profile.'
                        helpText='Changing this will update how we refer to this account e.g. in your receipts and communications. You can change how it gets represented as your funder or a planter profile.'
                      />
                      <FormField
                        id='Type'
                        label='Account type'
                        type={FormFieldTypes.select}
                        options={accountTypeOptions}
                        onChange={() => {
                          setFieldValue('Sector', '')
                          setFieldValue('BusinessType', '')
                          setFieldValue('SchoolType', '')
                          setFieldValue('OrgType', '')
                          setFieldValue('FocusArea', '')
                          setFieldValue('GovType', '')
                        }}
                      />
                      <AccountFormFieldSets
                        accountType={values.Type as AccountType}
                      />
                      {values.Type === AccountType.Business && (
                        <>
                          <FormField
                            id='BusinessSize'
                            label='Business size'
                            options={businessSizeOptions}
                            type={FormFieldTypes.select}
                          />
                          <FormField
                            id='Department'
                            label='Department'
                            options={departmentOptions}
                            type={FormFieldTypes.select}
                          />
                          <FormField
                            id='JobTitle'
                            label='Job title'
                            type={FormFieldTypes.text}
                          />
                        </>
                      )}
                      <FormField
                        id='Description'
                        label='Description'
                        type={FormFieldTypes.textarea}
                        rows={4}
                        description={
                          <ParagraphSmaller className='form-description-text whitespace-pre-line'>
                            This description also gets displayed in your funder
                            or planter profile pages on the website if you set
                            them to be public.{' '}
                            <InternalLink href={viewAccountProfilePath}>
                              View profile settings
                            </InternalLink>
                          </ParagraphSmaller>
                        }
                      />
                    </div>
                    <div>
                      <Heading3
                        id='contact'
                        className='text-[28px] leading-[39px] font-semibold m-0! mb-5!'
                      >
                        Contact information
                      </Heading3>
                      <div className='flex flex-col md:flex-row gap-0 md:gap-5'>
                        <FormField
                          id='ContactFirstname'
                          label='First name'
                          type={FormFieldTypes.text}
                        />
                        <FormField
                          id='ContactLastname'
                          label='Last name'
                          type={FormFieldTypes.text}
                        />
                      </div>
                      <FormField
                        id='Email'
                        label='Email'
                        type={FormFieldTypes.email}
                        description='Changing this will change who we communicate with regarding this account. It can be different from what you log in with.'
                      />
                      <FormField
                        id='Phone'
                        label='Phone'
                        type={FormFieldTypes.tel}
                      />
                      <FormField
                        id='Mobile'
                        label='Mobile'
                        type={FormFieldTypes.tel}
                      />
                      <FormField
                        id='ManualAddress'
                        type={FormFieldTypes.radioGroup}
                        label='Address'
                        options={[
                          { label: 'Search address', value: 'No' },
                          { label: 'Enter address manually', value: 'Yes' }
                        ]}
                        onChange={v => {
                          if (v.currentTarget.value === 'Yes')
                            setFieldValue('Address.Geo', null)
                        }}
                      />
                      {values.ManualAddress === 'No' ? (
                        <MapLocationSearch
                          autocompleteProps={{
                            id: 'Address.Geo',
                            label: 'Find address',
                            placeholder:
                              'Start typing an address or location name'
                          }}
                          geoData={
                            values.Address?.Geo
                              ? JSON.parse(values.Address?.Geo)
                              : { lat: 0, lng: 0, place_id: '' }
                          }
                          onPin={v => {
                            setRegionDistrict(v)
                            setFieldValue(
                              'Address.StreetNumber',
                              v.street_number
                            )
                            setFieldValue('Address.StreetName', v.street_name)
                            setFieldValue('Address.City', v.city)
                            setFieldValue('Address.Country', v.country)
                            setFieldValue('Address.PostalCode', v.postal_code)
                            setFieldValue('Address.Place', v.place_id)
                            setFieldValue('Address.Geo', JSON.stringify(v))
                            setFieldValue('Address.Region', v.region)
                            setFieldValue('Address.District', v.district)
                            setFieldValue('ManualAddress', 'No')
                          }}
                          showMap={false}
                        />
                      ) : (
                        <>
                          <FormField
                            className='w-1/3'
                            id='Address.StreetNumber'
                            label='Street number'
                            type={FormFieldTypes.text}
                          />
                          <FormField
                            id='Address.StreetName'
                            label='Street name'
                            type={FormFieldTypes.text}
                          />
                          <FormField
                            id='Address.City'
                            label='City'
                            type={FormFieldTypes.text}
                          />
                          <div className='lg:flex lg:space-x-2'>
                            <FormField
                              id='RegionId'
                              label='Region'
                              nullOption={{
                                label: 'Please select a region...',
                                value: 'none'
                              }}
                              type={FormFieldTypes.regionSelect}
                              regionsOnly={false}
                            />
                            <FormField
                              id='DistrictId'
                              label='District'
                              type={FormFieldTypes.districtSelect}
                              regionId={values.RegionId || ''}
                            />
                          </div>
                          <FormField
                            id='Address.Country'
                            label='Country'
                            type={FormFieldTypes.text}
                          />
                          <FormField
                            className='w-1/3'
                            id='Address.PostalCode'
                            label='Postal code'
                            type={FormFieldTypes.text}
                          />
                        </>
                      )}
                    </div>
                  </div>
                  <div className='flex flex-row items-center justify-between'>
                    <OnClickLink
                      className='text-forestDeep! underline'
                      onClick={() => router.push(viewAccountDetailPath)}
                    >
                      Cancel
                    </OnClickLink>
                    <ButtonRebrand
                      variant='lime'
                      type='submit'
                      disabled={!dirty}
                    >
                      Save changes
                    </ButtonRebrand>
                  </div>
                </Form>
              </GoogleMapsProvider>
            )
          }}
        </Formik>
      </FormLayoutProvider>
    </Card>
  )
}
